import { useEffect } from 'react';
import { getProductByHandle } from '@/lib/models/product';
import { ProductWithVariants } from '@/lib/models/types/product';
import { getProductInfo } from '@/lib/api.cms';
import Layout from '@/components/layout';
import { useRouter } from 'next/router';
import { ProductPageTemplate } from '@/components/templates';
import { META_ROBOTS_DEFAULT } from '@/lib/constants';
import { getLocales } from '@/lib/api.locale';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetStaticProps } from 'next';
import { getSettings } from '@/utils/Settings';
import { getProductCarousel } from '@/utils/Product';
import { getSettingsTheme } from '@/utils/SettingsTheme';

import { IMeta, IProductInfo, IData, IProductPage, IPage } from '@/types/index';
import { FetchError } from '@/utils/typings/errors';
import { isErr, Result } from '@/utils/typings/result';
import { IMirtaServerProps } from '@/types/IMirtaServerProps';
import { DEFAULT_CURRENCY } from '@/utils/Currency';
import { getBrandById } from '@/lib/api.brands';

const ProductPageA: React.FC<IPage<IProductPage>> = ({
  preview,
  data,
  locales,
  locale,
  currency,
  product,
  pageType,
  shippingInfo,
  readyToDispatch,
  toBeCrafted,
  brandSlug,
}) => {
  const router = useRouter();
  const isFallback = router.isFallback;

  useEffect(() => {
    router.beforePopState(() => {
      window.history.scrollRestoration = 'manual';
      return true;
    });
  }, []);

  if (!isFallback && !locales && !currency) {
    return <div>Loading</div>;
  }
  if (!locales) {
    return null;
  }

  if (!currency) {
    return null;
  }
  const availabilityInfos = {
    readyToDispatch,
    toBeCrafted,
  };

  return (
    <Layout
      layout={data.layout}
      preview={preview}
      locales={locales}
      locale={locale}
      pageType={pageType}
      currency={currency}
      productObj={product}>
      <ProductPageTemplate
        content={data.content}
        extraContent={data.extraContent}
        productObj={product}
        shippingInfo={shippingInfo ?? undefined}
        availabilityInfos={availabilityInfos}
        locale={locale}
        pageType={pageType}
        brandSlug={brandSlug}
      />
    </Layout>
  );
};

export default ProductPageA;

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps<
  IMirtaServerProps<IProductPage>
> = async context => {
  const { locale = 'en', params } = context;
  const handle = params?.handle;
  const preview = context.preview ? context.preview : false;
  const locales = getLocales();

  const currency = DEFAULT_CURRENCY;

  if (!handle) {
    return {
      notFound: true,
    };
  }

  let productCatalog: Result<
    ProductWithVariants,
    FetchError<any>
  > = await getProductByHandle(handle as string);

  if (isErr(productCatalog)) {
    return {
      notFound: true,
    };
  }

  const productInfo = (await getProductInfo(
    'product',
    'slug',
    handle as string,
    locale,
    preview
  )) as IProductInfo;

  const shippingInfo = productInfo?.fields?.shippingInfo ?? null;

  let fieldsContent = productInfo?.fields?.content?.fields?.section || [];

  fieldsContent = fieldsContent.map((f, i) => {
    return {
      ...f,
      mode: 'content',
      lastOne: i === fieldsContent.length - 1,
    };
  });

  const section = [
    ...(fieldsContent || []),
    ...(productInfo?.fields?.layout?.fields?.footer?.fields?.section || []),
  ];

  let meta: IMeta = {
    canonical: '',
    name: '',
    title: '',
  };

  if (
    productInfo &&
    productInfo.fields.meta &&
    productInfo.fields.meta.fields
  ) {
    if (productInfo.fields.meta.fields.title) {
      meta.title = productInfo.fields.meta.fields.title;
    }

    if (productInfo.fields.meta.fields.metaDescription) {
      meta.metaDescription = productInfo.fields.meta.fields.metaDescription;
    }
  }

  const product = productCatalog.result;

  if (product.websiteMetafields.title) {
    meta.title = product.websiteMetafields.title;
  }

  if (product.websiteMetafields.description) {
    meta.metaDescription = product.websiteMetafields.description.substring(
      0,
      155
    );
  }

  if (product.websiteMetafields.canonicalRelative) {
    meta['canonical'] = `${process.env.BASE_DOMAIN_URL}${
      locale !== context.defaultLocale ? '/' + locale : ''
    }/products/${product.websiteMetafields.canonicalRelative}`;
  }

  meta['robots'] = productInfo?.fields?.meta?.fields?.robots
    ? productInfo.fields.meta.fields.robots
    : META_ROBOTS_DEFAULT;

  const brand = await getBrandById(product.artisan_registry_id);
  let brandSlug = product.brand.id;
  if (!isErr(brand) && brand.result.data.name_slug) {
    brandSlug = brand.result.data.name_slug;
  }

  const productCarousel = await getProductCarousel(
    section,
    product.brand.id,
    handle as string
  );

  const data: IData = {
    layout: {
      header: productInfo?.fields?.layout?.fields?.header?.fields || null,
      footer: productInfo?.fields?.layout?.fields?.footer?.fields || null,
      meta: meta,
    },
    content: [...section],
    extraContent: {
      productCarousel,
    },
  };

  const settings = getSettings(data);

  const settingsTheme = getSettingsTheme();

  return {
    props: {
      data,
      preview,
      product,
      shippingInfo,
      readyToDispatch: productInfo?.fields?.readyToDispatch ?? null,
      toBeCrafted: productInfo?.fields?.toBeCrafted ?? null,
      locales: locales.items,
      locale,
      pageType: 'productPage',
      settings: settings,
      settingsTheme,
      currency,
      ...(await serverSideTranslations(locale, [
        'product',
        'account',
        'common',
      ])),
      brandSlug: brandSlug,
    },
    revalidate: 300, // In seconds
  };
};
